import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import HelpIcon from '@material-ui/icons/Help';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import FlagIcon from '@material-ui/icons/Flag';
import DescriptionIcon from '@material-ui/icons/Description';
import {
  DomainAssessment,
  AuditControl,
  AuditDomainLabel,
  AuditDomainWHOBlock,
  MaturityLevel,
  MaturityLevelLabel,
  ComplianceStatus,
} from '../../types';
import { auditService } from '../../services/auditService';

const useStyles = makeStyles(theme => ({
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': { display: 'none' },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  domainHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexGrow: 1,
  },
  domainTitle: {
    fontWeight: 600,
    minWidth: 240,
  },
  domainMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  maturityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    fontWeight: 'bold',
    fontSize: '0.75rem',
    color: '#fff',
  },
  scoreBadge: {
    fontWeight: 'bold',
    fontSize: '1rem',
    minWidth: 50,
    textAlign: 'right',
  },
  detailSection: {
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  infoCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  controlRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  statusCompliant: { color: theme.palette.success.main },
  statusPartial: { color: theme.palette.warning.main },
  statusNonCompliant: { color: theme.palette.error.main },
  statusNotAssessed: { color: theme.palette.grey[500] },
  gapItem: {
    color: theme.palette.error.main,
    fontSize: '0.85rem',
  },
  recItem: {
    color: theme.palette.info.main,
    fontSize: '0.85rem',
  },
  targetArrow: {
    color: theme.palette.success.main,
    verticalAlign: 'middle',
    fontSize: 16,
    marginRight: 4,
  },
}));

const getStatusIcon = (status: ComplianceStatus) => {
  switch (status) {
    case 'compliant':
      return <CheckCircleIcon style={{ color: '#4caf50' }} />;
    case 'partial':
      return <WarningIcon style={{ color: '#ff9800' }} />;
    case 'non-compliant':
      return <ErrorIcon style={{ color: '#f44336' }} />;
    case 'not-assessed':
      return <HelpIcon style={{ color: '#9e9e9e' }} />;
  }
};

const getStatusLabel = (status: ComplianceStatus) => {
  switch (status) {
    case 'compliant':
      return 'Compliant';
    case 'partial':
      return 'Partial';
    case 'non-compliant':
      return 'Non-Compliant';
    case 'not-assessed':
      return 'Not Assessed';
  }
};

const getMaturityColor = (level: MaturityLevel) => {
  if (level >= 4) return '#4caf50';
  if (level >= 3) return '#ff9800';
  if (level >= 2) return '#f44336';
  return '#d32f2f';
};

export const DomainAssessmentView = () => {
  const classes = useStyles();
  const [domains, setDomains] = useState<DomainAssessment[]>([]);
  const [controls, setControls] = useState<AuditControl[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | false>(false);

  useEffect(() => {
    const load = async () => {
      const [d, c] = await Promise.all([
        auditService.getDomainAssessments(),
        auditService.getControls(),
      ]);
      setDomains(d);
      setControls(c);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <LinearProgress />;

  const handleAccordionChange =
    (panel: string) => (_: any, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Domain Assessments — WHO Digital Health Framework
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Each domain is assessed against the WHO MAPS toolkit and scored across{' '}
        {controls.length} controls. Maturity levels range from Level 1 (Ad-hoc)
        to Level 5 (Optimizing).
      </Typography>

      {domains.map(domain => {
        const domainControls = controls.filter(c => c.domain === domain.domain);
        return (
          <Accordion
            key={domain.domain}
            className={classes.accordion}
            expanded={expanded === domain.domain}
            onChange={handleAccordionChange(domain.domain)}
            variant="outlined"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              <div className={classes.domainHeader}>
                <Typography className={classes.domainTitle}>
                  {AuditDomainLabel[domain.domain]}
                </Typography>
                <div className={classes.domainMeta}>
                  <span
                    className={classes.maturityBadge}
                    style={{
                      backgroundColor: getMaturityColor(domain.currentMaturity),
                    }}
                  >
                    L{domain.currentMaturity} —{' '}
                    {MaturityLevelLabel[domain.currentMaturity]}
                  </span>
                  <Chip
                    size="small"
                    icon={<ArrowUpwardIcon className={classes.targetArrow} />}
                    label={`Target: L${domain.targetMaturity}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${domain.controlsCompliant}/${domain.controlsTotal} compliant`}
                    variant="outlined"
                  />
                  {domain.criticalFindings > 0 && (
                    <Chip
                      size="small"
                      icon={<FlagIcon />}
                      label={`${domain.criticalFindings} critical`}
                      style={{ backgroundColor: '#f44336', color: '#fff' }}
                    />
                  )}
                </div>
              </div>
              <Typography
                className={classes.scoreBadge}
                style={{ color: domain.score >= 50 ? '#4caf50' : '#f44336' }}
              >
                {domain.score}%
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box width="100%">
                {/* Info row */}
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                  <Grid item xs={12} md={4}>
                    <Card className={classes.infoCard} variant="outlined">
                      <Typography variant="subtitle2">
                        WHO Building Block
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {AuditDomainWHOBlock[domain.domain]}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card className={classes.infoCard} variant="outlined">
                      <Typography variant="subtitle2">Maturity Gap</Typography>
                      <Typography variant="body2">
                        Current: <strong>L{domain.currentMaturity}</strong> (
                        {MaturityLevelLabel[domain.currentMaturity]})
                        &nbsp;→&nbsp; Target:{' '}
                        <strong>L{domain.targetMaturity}</strong> (
                        {MaturityLevelLabel[domain.targetMaturity]})
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={
                          (domain.currentMaturity / domain.targetMaturity) * 100
                        }
                        style={{ marginTop: 8, height: 8, borderRadius: 4 }}
                      />
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card className={classes.infoCard} variant="outlined">
                      <Typography variant="subtitle2">
                        Assessment Info
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Assessed: {domain.lastAssessedDate}
                        <br />
                        Assessor: {domain.assessor}
                        <br />
                        Weight: {domain.weightPercent}% of overall score
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Notes */}
                <Card className={classes.infoCard} variant="outlined">
                  <Typography variant="subtitle2" gutterBottom>
                    Assessment Notes
                  </Typography>
                  <Typography variant="body2">{domain.notes}</Typography>
                </Card>

                {/* Controls table */}
                <Typography
                  variant="subtitle1"
                  className={classes.sectionLabel}
                >
                  Controls ({domainControls.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ref</TableCell>
                        <TableCell>Control</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>WHO Reference</TableCell>
                        <TableCell>Responsible</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {domainControls.map(ctrl => (
                        <React.Fragment key={ctrl.id}>
                          <TableRow className={classes.controlRow}>
                            <TableCell>
                              <strong>{ctrl.controlRef}</strong>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 500 }}
                              >
                                {ctrl.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {ctrl.description.length > 120
                                  ? `${ctrl.description.substring(0, 120)}…`
                                  : ctrl.description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 4 }}
                              >
                                {getStatusIcon(ctrl.complianceStatus)}
                                <Typography variant="body2">
                                  {getStatusLabel(ctrl.complianceStatus)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={ctrl.priority}
                                size="small"
                                style={{
                                  backgroundColor:
                                    ctrl.priority === 'critical'
                                      ? '#d32f2f'
                                      : ctrl.priority === 'high'
                                      ? '#f44336'
                                      : ctrl.priority === 'medium'
                                      ? '#ff9800'
                                      : '#2196f3',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {ctrl.whoReference}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {ctrl.responsibleParty}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {/* Gaps & Recommendations sub-row */}
                          {(ctrl.gaps.length > 0 ||
                            ctrl.recommendations.length > 0) && (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                style={{ paddingTop: 0, paddingBottom: 8 }}
                              >
                                <Grid container spacing={2}>
                                  {ctrl.gaps.length > 0 && (
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="caption"
                                        style={{
                                          fontWeight: 600,
                                          color: '#f44336',
                                        }}
                                      >
                                        Gaps:
                                      </Typography>
                                      <List dense disablePadding>
                                        {ctrl.gaps.map((g, i) => (
                                          <ListItem
                                            key={i}
                                            dense
                                            disableGutters
                                            style={{
                                              paddingTop: 0,
                                              paddingBottom: 0,
                                            }}
                                          >
                                            <ListItemIcon
                                              style={{ minWidth: 20 }}
                                            >
                                              <ErrorIcon
                                                style={{
                                                  fontSize: 12,
                                                  color: '#f44336',
                                                }}
                                              />
                                            </ListItemIcon>
                                            <ListItemText
                                              primary={
                                                <Typography
                                                  className={classes.gapItem}
                                                >
                                                  {g}
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </Grid>
                                  )}
                                  {ctrl.recommendations.length > 0 && (
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="caption"
                                        style={{
                                          fontWeight: 600,
                                          color: '#2196f3',
                                        }}
                                      >
                                        Recommendations:
                                      </Typography>
                                      <List dense disablePadding>
                                        {ctrl.recommendations.map((r, i) => (
                                          <ListItem
                                            key={i}
                                            dense
                                            disableGutters
                                            style={{
                                              paddingTop: 0,
                                              paddingBottom: 0,
                                            }}
                                          >
                                            <ListItemIcon
                                              style={{ minWidth: 20 }}
                                            >
                                              <DescriptionIcon
                                                style={{
                                                  fontSize: 12,
                                                  color: '#2196f3',
                                                }}
                                              />
                                            </ListItemIcon>
                                            <ListItemText
                                              primary={
                                                <Typography
                                                  className={classes.recItem}
                                                >
                                                  {r}
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </Grid>
                                  )}
                                </Grid>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
